import type { LucideIcon } from "lucide-react";
import {
  PawPrint,
  Images,
  MapPin,
  Users,
  MessageCircle,
  AlertTriangle,
  ShieldCheck,
  Database,
  EyeOff,
  Trash2,
  Sparkles,
  Camera,
  Heart,
} from "lucide-react";

export interface NavLink {
  label: string;
  href: string;
}

// Hrefs are root-absolute (`/#anchor`) so they work from any page — on the
// home page they just scroll (same path, hash only); from a guide page they
// navigate home and then scroll. `/ratgeber` is a real separate page.
export const navLinks: NavLink[] = [
  { label: "Funktionen", href: "/#funktionen" },
  { label: "So geht's", href: "/#so-gehts" },
  { label: "Sicherheit", href: "/#sicherheit" },
  { label: "Ratgeber", href: "/ratgeber" },
  { label: "FAQ", href: "/#faq" },
];

export interface FeatureItem {
  id: string;
  title: string;
  description: string;
  Icon: LucideIcon;
  accent: "primary" | "secondary" | "tertiary";
  span: "tall" | "wide" | "normal";
  /** Optional "mehr erfahren" link to a dedicated feature or guide page. */
  href?: string;
}

export const features: FeatureItem[] = [
  {
    id: "profile",
    title: "Das Profil deines Tieres",
    description:
      "Eigene Seite mit Fotos, Steckbrief, Rasse und Bio — mehrere Halter pro Profil möglich. Der Star bist du nicht, dein Hund ist es.",
    Icon: PawPrint,
    accent: "primary",
    span: "tall",
  },
  {
    id: "feed",
    title: "Feed mit Foto & Video",
    description: "Posts und Reels in Hochformat — mit EXIF-Strip, HD-Transcoding und stummem Auto-Play.",
    Icon: Images,
    accent: "secondary",
    span: "wide",
  },
  {
    id: "nearby",
    title: "Tierfreunde in der Nähe",
    description:
      "Opt-in-Standortsuche mit wählbarem Radius. Sieh, wer heute im Park ist — ganz ohne Dark Patterns.",
    Icon: MapPin,
    accent: "primary",
    span: "normal",
  },
  {
    id: "groups",
    title: "Gruppen & Communities",
    description: "Nach Rasse, Region oder Interesse. Plane Spaziergänge, tausch dich aus, finde Gleichgesinnte.",
    Icon: Users,
    accent: "secondary",
    span: "normal",
  },
  {
    id: "chat",
    title: "Private Chats",
    description: "1:1 und Gruppen in Echtzeit. Push-Benachrichtigungen, wenn du gerade nicht in der App bist.",
    Icon: MessageCircle,
    accent: "tertiary",
    span: "wide",
  },
  {
    id: "hazard",
    title: "Gefahren-Warnungen",
    description:
      "Community-getriebene Warnkarte: Giftköder, Fallen, aggressive Hunde, Straßengefahren — und vermisste Tiere mit wählbarem Suchradius.",
    Icon: AlertTriangle,
    accent: "tertiary",
    span: "normal",
    href: "/funktionen/warnkarte",
  },
];

export interface StepItem {
  number: string;
  title: string;
  description: string;
  Icon: LucideIcon;
}

export const steps: StepItem[] = [
  {
    number: "01",
    title: "Profil anlegen",
    description: "Erzähl uns von deinem Liebling — Name, Rasse, ein paar Fotos. Dauert keine zwei Minuten.",
    Icon: Camera,
  },
  {
    number: "02",
    title: "Vernetzen",
    description: "Finde andere Fellnasen in deiner Nachbarschaft oder Gruppen zu eurer Lieblingsrasse.",
    Icon: Heart,
  },
  {
    number: "03",
    title: "Erleben & teilen",
    description: "Poste Schnappschüsse, plane Treffen, chatte direkt mit anderen Haltern. Fertig.",
    Icon: Sparkles,
  },
];

export interface TrustPillar {
  title: string;
  description: string;
  Icon: LucideIcon;
}

export const trustPillars: TrustPillar[] = [
  {
    title: "Self-hosted in Deutschland",
    description:
      "Unsere Server stehen in Liebenscheid, Rheinland-Pfalz — einem Dorf im Westerwald. Deine Daten verlassen das Land nie. Keine US-Cloud, keine Drittanbieter.",
    Icon: ShieldCheck,
  },
  {
    title: "Volle Datenhoheit",
    description:
      "Auskunft über alle zu dir gespeicherten Daten auf Anfrage — formlos per E-Mail, wie es die DSGVO vorschreibt (Art. 15).",
    Icon: Database,
  },
  {
    title: "Kein Datenverkauf",
    description: "Wir verkaufen deine Daten nicht an Dritte — auch nicht zu Werbezwecken. Punkt.",
    Icon: EyeOff,
  },
  {
    title: "Löschen heißt löschen",
    description:
      "Konto löschen entfernt sofort deine Profildaten, Beiträge und Medien aus der aktiven Nutzung.",
    Icon: Trash2,
  },
];

export interface FaqItem {
  question: string;
  answer: string;
}

/**
 * FAQ content. IMPORTANT: keep this in sync with the FAQPage JSON-LD block in
 * `index.html` — search engines read the structured data there, users read
 * this. Drift between the two can cause rich-result penalties.
 */
export const faqs: FaqItem[] = [
  {
    question: "Was ist Welope?",
    answer:
      "Welope ist das soziale Netzwerk für Haustiere: eigene Profile für Hunde, Katzen und Co., ein Feed mit Fotos und Videos, Gruppen, private Chats und eine Community, die Tiere genauso liebt wie du.",
  },
  {
    question: "Was kostet Welope?",
    answer:
      "Welope ist kostenlos nutzbar. Wir verkaufen deine Daten nicht an Dritte — der Betrieb finanziert sich unter anderem über Werbung, die wir so zurückhaltend wie möglich gestalten.",
  },
  {
    question: "Ist Welope DSGVO-konform?",
    answer:
      "Ja. Welope wird selbst gehostet in Deutschland — unsere Server stehen in Liebenscheid, Rheinland-Pfalz. Deine Daten verlassen das Land nie und werden nicht an Dritte verkauft. Auskunft und Löschung sind jederzeit formlos möglich (DSGVO Art. 15).",
  },
  {
    question: "Für welche Tiere ist Welope gedacht?",
    answer:
      "Für alle Lieblinge — Hunde, Katzen und Co. Jedes Tier bekommt sein eigenes Profil mit Steckbrief, Rasse und Bio, das mehrere Halter gemeinsam pflegen können.",
  },
  {
    question: "Sammelt Welope meinen Standort?",
    answer:
      "Nur wenn du es möchtest. Die Standortsuche „Tierfreunde in der Nähe“ ist Opt-in mit frei wählbarem Radius — ganz ohne Dark Patterns. Ohne deine Zustimmung wird kein Standort verarbeitet.",
  },
  {
    question: "Wann kann ich mich anmelden?",
    answer:
      "Welope ist gerade im Aufbau. Trag dich auf die Warteliste ein — wir melden uns per E-Mail, sobald du loslegen kannst.",
  },
];

/**
 * Canonical "sign up" call-to-action target.
 *
 * Welope itself is still gated behind a "coming soon" password wall
 * (see apps/web's middleware.ts) — linking straight to welope.de/login
 * would send visitors into an unexplained password prompt. Every
 * "Jetzt starten", "Anmelden", "Kostenlos registrieren" or equivalent CTA
 * instead scrolls to the waitlist form (#warteliste in CTA.tsx), which
 * collects an email via waitlist.join and is the actual, working funnel
 * until the app is ready to link to directly.
 */
export const REGISTER_URL = "/#warteliste";
